import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import {
  FaPaintBrush,
  FaServer,
  FaDatabase,
  FaReact,
  FaRocket,
  FaPhp,
  FaAmazon,
  FaMicrosoft
} from 'react-icons/fa';
import SEO from '../components/SEO';

const Services: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const services = [
    {
      icon: FaReact,
      title: 'Frontend Development',
      description: 'Building responsive, interactive UIs with React.js, Tailwind CSS, and TypeScript',
      color: '#61DAFB',
      features: ['React.js', 'Tailwind CSS', 'TypeScript', 'HTML5/CSS3']
    },
    {
      icon: FaServer,
      title: 'Backend Development',
      description: 'Creating scalable APIs with Node.js, Express.js, and PHP',
      color: '#339933',
      features: ['Node.js', 'Express.js', 'PHP', 'REST APIs']
    },
    {
      icon: FaDatabase,
      title: 'Database Design',
      description: 'Designing and optimizing both NoSQL and SQL databases',
      color: '#47A248',
      features: ['MongoDB', 'MySQL', 'Schema Design', 'Optimization']
    },
    {
      icon: FaPhp,
      title: 'PHP Development',
      description: 'Building robust web applications with PHP and Laravel',
      color: '#777BB4',
      features: ['PHP', 'Laravel', 'Custom PHP', 'MySQL Integration']
    },
    {
      icon: FaPaintBrush,
      title: 'Graphic & UI/UX Design',
      description: 'Creating beautiful, intuitive interfaces and stunning visuals',
      color: '#F24E1E',
      features: ['Canva Pro', 'Photoshop', 'Wireframing', 'Prototyping', 'User Testing']
    },
    {
      icon: FaAmazon,
      title: 'Amazon Account Management',
      description: 'End-to-end Amazon FBA, FBM, and listing optimization',
      color: '#FF9900',
      features: ['FBA', 'FBM', 'Listing Optimization', 'Account Management']
    },
    {
      icon: FaMicrosoft,
      title: 'MS Office Solutions',
      description: 'Professional MS Office solutions for organized, impactful work',
      color: '#00A4EF',
      features: ['Word', 'Excel', 'PowerPoint', 'Data Analysis']
    },
    {
      icon: FaRocket,
      title: 'Full Stack Architecture',
      description: 'Designing end-to-end architecture with modern tech stacks',
      color: '#6C63FF',
      features: ['MERN Stack', 'PHP/MySQL', 'Cloud Deployment', 'Security']
    },
  ];

  return (
    <>
      <SEO 
        title="Services" 
        description="Professional services - Frontend, Backend, PHP Development, Amazon VA, MS Office Solutions, and more" 
      />
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 pt-24">
        {/* Services Header */}
        <section className="py-12">
          <div className="container-custom px-4">
            <motion.div
              ref={ref}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <div className="inline-block px-4 py-1 bg-primary/10 rounded-full text-primary text-sm font-medium mb-4">
                What I Offer
              </div>
              <h1 className="text-4xl md:text-5xl font-display font-bold text-primary mb-4">
                My <span className="text-gray-800 dark:text-gray-100">Services</span>
              </h1>
              <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
                Comprehensive development and professional services to bring your ideas to life
              </p>
              <div className="w-24 h-1 bg-gradient-to-r from-primary to-secondary mx-auto rounded-full mt-4" />
            </motion.div>

            <div id="services" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.05 }}
                  className="group"
                >
                  <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105 h-full">
                    {/* Icon */}
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110"
                      style={{ backgroundColor: `${service.color}20` }}
                    >
                      <service.icon className="text-2xl" style={{ color: service.color }} />
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-lg font-display font-bold text-gray-800 dark:text-gray-100 mb-2">
                      {service.title}
                    </h3>
                    
                    {/* Description */}
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
                      {service.description}
                    </p>
                    
                    {/* Features */}
                    <div className="flex flex-wrap gap-2">
                      {service.features.map((feature, idx) => (
                        <span 
                          key={idx}
                          className="text-xs px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-gray-600 dark:text-gray-300"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16">
          <div className="container-custom px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-r from-primary to-gray-800 rounded-3xl p-12 text-center text-white shadow-2xl"
            >
              <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
                Ready to Build Something Amazing?
              </h2>
              <p className="text-white/90 max-w-2xl mx-auto mb-8">
                Let's collaborate and create exceptional digital experiences together.
              </p>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/contact"
                  className="inline-block px-8 py-3 bg-white dark:bg-gray-800 text-primary rounded-xl font-medium shadow-lg hover:shadow-2xl transition-all duration-300"
                >
                  Get in Touch
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Services;